import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import VideoCard from '@/components/feature/VideoCard';
import { heroSlides, categories, featuredVideos, hotRankings, latestUpdates } from '@/mocks/data';

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, []);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [isPaused, nextSlide]);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden" aria-label="精选推荐">
          {heroSlides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover object-top"
                title={`${slide.title} - 小蓝视频`}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
            </div>
          ))}

          <div className="absolute inset-0 flex items-end">
            <div className="w-full px-4 md:px-6 pb-12 md:pb-16">
              <div className="max-w-7xl mx-auto w-full">
                <div
                  key={currentSlide}
                  className="animate-[fadeInUp_0.6s_ease-out]"
                >
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-white mb-3">
                    {heroSlides[currentSlide].category}
                  </span>
                  <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-2">
                    {heroSlides[currentSlide].title}
                  </h1>
                  <p className="text-sm md:text-base text-white/80 mb-4 max-w-xl">
                    {heroSlides[currentSlide].subtitle}
                  </p>
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1 text-sm text-accent-500">
                      <i className="ri-star-fill"></i>
                      {heroSlides[currentSlide].rating}
                    </span>
                    <Link
                      to={`/article/${heroSlides[currentSlide].id}`}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-play-fill"></i>
                      立即观看
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
                className={`w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${
                  index === currentSlide
                    ? 'bg-primary-500 w-8'
                    : 'bg-white/50 hover:bg-white/80'
                }`}
                aria-label={`切换到第${index + 1}张`}
              />
            ))}
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50" aria-label="视频分类">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">海量视频分类</h2>
              <p className="text-sm text-foreground-500 mt-2">电影、电视剧、综艺、动漫、纪录片，总有你想看的</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-3 p-5 md:p-6 rounded-lg bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-secondary-100 flex items-center justify-center group-hover:bg-primary-100 transition-colors">
                    <i className={`${cat.icon} text-xl md:text-2xl text-secondary-600 group-hover:text-primary-500 transition-colors`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Videos */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100" aria-label="精选推荐">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">精选推荐</h2>
                <p className="text-sm text-foreground-500 mt-1">编辑精选，品质保证</p>
              </div>
              <Link
                to="/category/dianying"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                更多推荐
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {featuredVideos.map((video) => (
                <VideoCard key={video.id} {...video} />
              ))}
            </div>
          </div>
        </section>

        {/* Hot Rankings + Latest Updates */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              {/* Hot Rankings */}
              <div className="lg:col-span-2">
                <div className="mb-6">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">热门排行</h2>
                  <p className="text-sm text-foreground-500 mt-1">实时热度排行榜</p>
                </div>
                <div className="bg-background-100 rounded-lg border border-background-200/70 overflow-hidden">
                  {hotRankings.map((item, index) => (
                    <Link
                      key={item.rank}
                      to={`/article/${item.rank}`}
                      className={`flex items-center gap-3 px-4 py-3 hover:bg-background-200/50 transition-colors cursor-pointer ${
                        index < hotRankings.length - 1 ? 'border-b border-background-200/70' : ''
                      }`}
                    >
                      <span className={`w-7 h-7 rounded flex items-center justify-center text-xs font-bold ${
                        item.rank <= 3
                          ? 'bg-primary-500 text-white'
                          : 'bg-secondary-100 text-secondary-600'
                      }`}>
                        {item.rank}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground-900 truncate">{item.title}</p>
                        <p className="text-xs text-foreground-400 mt-0.5">{item.category}</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-medium text-accent-500 whitespace-nowrap">{item.hot}</span>
                        <i className={`text-xs ${item.trend === 'up' ? 'ri-arrow-up-line text-green-500' : 'ri-arrow-down-line text-red-400'}`}></i>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Latest Updates */}
              <div className="lg:col-span-3">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">最近更新</h2>
                    <p className="text-sm text-foreground-500 mt-1">最新上线的热门内容</p>
                  </div>
                  <Link
                    to="/category/dianshiju"
                    className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    全部更新
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                  {latestUpdates.map((video) => (
                    <Link
                      key={video.id}
                      to={`/article/${video.id}`}
                      className="group flex gap-3 p-3 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-200 hover:bg-background-200/50 transition-all cursor-pointer"
                    >
                      <div className="relative w-28 sm:w-32 aspect-video rounded-md overflow-hidden flex-shrink-0">
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          title={`${video.title} - 小蓝视频`}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                        <div className="absolute bottom-1.5 right-1.5">
                          <span className="px-1.5 py-0.5 rounded text-[10px] bg-black/60 text-white">{video.quality}</span>
                        </div>
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <div className="w-8 h-8 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center">
                            <i className="ri-play-fill text-white text-sm ml-0.5"></i>
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col justify-center">
                        <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {video.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-xs px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-600 whitespace-nowrap">
                            {video.category}
                          </span>
                          <span className="text-xs text-foreground-400">{video.views}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-foreground-500">
                            <i className="ri-star-fill text-accent-500 text-[10px] mr-0.5"></i>
                            {video.rating}
                          </span>
                          <span className="text-xs text-primary-500 whitespace-nowrap">{video.updateInfo}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-primary-500" aria-label="加入我们">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
              高清不缓冲，免费看全集
            </h2>
            <p className="text-sm md:text-base text-white/80 mb-6 max-w-xl mx-auto">
              小蓝视频聚合91视频、芒果视频、91在线视频、菠萝视频、51视频等多家优质平台资源，最新电影电视剧随心看。
            </p>
            <Link
              to="/category/dianying"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-primary-600 hover:bg-white/90 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-lg"></i>
              开始观看
            </Link>
          </div>
        </section>
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "小蓝视频",
          "url": "https://www.xiaolanvideo.com/",
          "description": "小蓝视频承接91视频网站、芒果视频、91在线视频等平台，高清不缓冲在线观看，菠萝视频、51视频免费全集，最新电影电视剧随心看。",
          "foundingDate": "2018",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "北京",
            "addressCountry": "CN",
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "400-888-0000",
            "email": "contact@xiaolanvideo.com",
            "contactType": "customer service",
          },
        }),
      }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "小蓝视频 - 首页",
          "description": "小蓝视频承接91视频网站、芒果视频、91在线视频等平台，高清不缓冲在线观看，菠萝视频、51视频免费全集，最新电影电视剧随心看。",
          "url": "https://www.xiaolanvideo.com/",
          "isPartOf": {
            "@type": "WebSite",
            "name": "小蓝视频",
            "url": "https://www.xiaolanvideo.com/",
          },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "首页",
                "item": "https://www.xiaolanvideo.com/",
              },
            ],
          },
        }),
      }} />

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
}